// Effective capabilities for the signed-in member + admin writers.
//
// Read side: orgs/{orgId} (capabilityDefaults, modelPolicy) and the member's
// own orgs/{orgId}/members/{encodedEmail} doc (capabilities); both are
// member-readable under firestore.rules. Resolution mirrors
// services/llm-proxy/capabilities.js and every service's capabilities.py:
// member override, else org default, else allowed. Fails OPEN (all allowed)
// on any error: the UI gate is convenience, the services enforce.
//
// Write side: through llm-proxy (Admin SDK, validated against the registry,
// audit-logged), the same path member management already uses; rules keep
// these fields client-unwritable.
import { doc, getDoc } from 'firebase/firestore';
import { firestoreDb } from '../config/firebase';
import { encodeEmail, postMembership } from './orgs';
import { resolveEffectiveCapabilities, normalizeModelPolicy } from '../config/capabilities';

export const ALL_ALLOWED = Object.freeze({ denied: new Set(), modelPolicy: normalizeModelPolicy({}), orgId: null, loaded: true });

export async function loadEffectiveCapabilities({ orgId, email, org = null, superadmin = false } = {}) {
  if (superadmin || !firestoreDb || !orgId) {
    return { ...ALL_ALLOWED, denied: new Set(), modelPolicy: normalizeModelPolicy(org?.modelPolicy), orgId: orgId || null };
  }
  try {
    let orgDoc = org;
    if (!orgDoc) {
      const snap = await getDoc(doc(firestoreDb, 'orgs', orgId));
      orgDoc = snap.exists() ? snap.data() : {};
    }
    let overrides = {};
    if (email) {
      const m = await getDoc(doc(firestoreDb, 'orgs', orgId, 'members', encodeEmail(email)));
      overrides = (m.exists() && m.data().capabilities) || {};
    }
    const { denied } = resolveEffectiveCapabilities({ defaults: orgDoc?.capabilityDefaults || {}, overrides });
    return { denied, modelPolicy: normalizeModelPolicy(orgDoc?.modelPolicy), orgId, loaded: true };
  } catch (_) {
    return { ...ALL_ALLOWED, denied: new Set(), modelPolicy: normalizeModelPolicy({}), orgId, loaded: true };
  }
}

export function setOrgCapabilityDefaults(orgId, capabilityDefaults) {
  return postMembership('/admin/set-org-capabilities', { orgId, capabilityDefaults });
}

export function setMemberCapabilities(orgId, email, capabilities) {
  return postMembership('/membership/set-capabilities', { orgId, email, capabilities });
}

export function setOrgModelPolicy(orgId, modelPolicy) {
  return postMembership('/admin/set-model-policy', { orgId, modelPolicy });
}
